import { subDays, isToday, startOfDay } from 'date-fns';
import { format as numFormat } from '@awp/helpers/numFormat';
import { mapArrayToObjectReducer } from '@awp/helpers/Array';
import { isValueEmpty, isValueNotEmpty, isIntStr } from '@awp/helpers/utilsReact';
import { RISK_CODE_COLOURS, DATASET_TYPES } from '@awp/constants';

import { externalTooltipHandler } from '@graphs/SingleStation/TooltipHelper';
import { transformAxisInfo } from '@awp/graphs/transform/transforms';

import { Interaction, Tooltip } from 'chart.js';
import { getRelativePosition } from 'chart.js/helpers';

/**
 * Custom positioner
 * @function Tooltip.positioners.myCustomPositioner
 * @param elements {Chart.Element[]} the tooltip elements
 * @param eventPosition {Point} the position of the event in canvas coordinates
 * @returns {TooltipPosition} the tooltip position
 */
Tooltip.positioners.myCustomPositioner = function (elements, eventPosition) {
  const tooltip = this;
  const chartArea = tooltip.chart.chartArea;

  //determine if there are active elements
  if (!elements.length) {
    return false;
  }

  // //determine if the mouse is actually hovered over the visible graph area
  if (!isPositionVisible({ x: eventPosition.x, y: eventPosition.y }, chartArea)) {
    return false;
  }

  return {
    x: eventPosition.x,
    y: eventPosition.y,
    // You may also include xAlign and yAlign to override those tooltip options.
  };
};

/**
 * depth based y-axis interaction mode
 * @function Interaction.modes.depth
 * @param {Chart} chart - the chart we are returning items from
 * @param {Event} e - the event we are find things at
 * @param {InteractionOptions} options - options to use
 * @param {boolean} [useFinalPosition] - use final element position (animation target)
 * @return {InteractionItem[]} - items that are found
 */
// eslint-disable-next-line no-unused-vars
Interaction.modes.depth = function (chart, e, options, useFinalPosition) {
  const position = getRelativePosition(e, chart);

  const items = [];
  //determine if the mouse is actually hovered over the visible graph area
  if (!isPositionVisible({ x: position.x, y: position.y }, chart.chartArea)) {
    return items;
  }

  chart._getSortedDatasetMetas().map((ds, i) => {
    //get the timestamp, that the y mouse coordinate translates too
    const depth = ds.yScale.getValueForPixel(position['y']);

    //find the the parsed value of the item at the moused over depth
    //const dataIdx = ds._parsed.map(d => d.y === depth).findIndex(x => x === true);
    const dataIdx = ds._parsed.findIndex(
      (d) =>
        d ===
        ds._parsed.reduce((p, c) => {
          return Math.abs(c.y - depth) < Math.abs(p.y - depth) ? c : p;
        }),
    );

    // if no index found, do nothing
    if (dataIdx !== -1) {
      const element = ds.data[dataIdx];

      const customData = {
        type: ds.type,
        data: ds._dataset.data[dataIdx],
        yAxisList: ds._dataset.yAxisList,
        datasetName: ds._dataset.datasetName,
        groupName: ds._dataset.groupName,
        unitLabel: ds._dataset.unitLabel,
        legendColor: ds._dataset.backgroundColor,
      };
      // push the element with the dataset index and the data index
      items.push({ element, customData, datasetIndex: i, index: dataIdx });
    }
  });

  return items;
};

/**
 * timestamp based x-axis interaction mode
 * @function Interaction.modes.timestamp
 * @param {Chart} chart - the chart we are returning items from
 * @param {Event} e - the event we are find things at
 * @param {InteractionOptions} options - options to use
 * @param {boolean} [useFinalPosition] - use final element position (animation target)
 * @return {InteractionItem[]} - items that are found
 */
// eslint-disable-next-line no-unused-vars
Interaction.modes.timestamp = function (chart, e, options, useFinalPosition) {
  const position = getRelativePosition(e, chart);

  const items = [];

  //determine if the mouse is actually hovered over the visible graph area
  if (!isPositionVisible({ x: position.x, y: position.y }, chart.chartArea)) {
    return items;
  }

  //get the minimum collection interval
  const ci = Math.min(
    ...chart._getSortedDatasetMetas().map((ds) => {
      return ds._dataset.collectionInterval || 60;
    }),
  );

  chart._getSortedDatasetMetas().map((ds, i) => {
    //get the timestamp, that the x mouse coordinate translates too
    const eventDate = new Date(ds.xScale.getValueForPixel(position['x']));
    //use the collection interval to determine the closest point
    //const interval = ds._dataset.collectionInterval;
    const roundedTimestamp = Math.round(eventDate.getTime() / (ci * 60 * 1000)) * (ci * 60 * 1000);

    //find the the parsed value of the item at the moused over timestamp
    const dataIdx = ds._parsed.map((d) => d.x === roundedTimestamp).findIndex((x) => x === true);

    // if no index found, do nothing
    if (dataIdx !== -1) {
      //element is the point that is highlighted
      const element = ds.data[dataIdx];

      //heat map and regular line items build their elements differently
      if (ds._dataset.type != 'TemperatureProfile') {
        //regular line/bar item
        const customData = {
          type: ds.type,
          data: ds._dataset.data[dataIdx],
          yAxisList: ds._dataset.yAxisList,
          datasetName: ds._dataset.datasetName,
          groupName: ds._dataset.groupName,
          unitLabel: ds._dataset.unitLabel,
          legendColor: ds._dataset.legendColor,
          weatherStationInfo: ds._dataset.weatherStationInfo,
          sensorType: ds._dataset.sensorType,
        };
        // push the element with the dataset index and the data index
        items.push({ element, customData, datasetIndex: i, index: dataIdx });
      } else {
        //get the show invalid data flag
        const showInvalidData = ds.yScale.options.showInvalidData || false;
        // heat map needs the depth, get the depth based on the y mouse position
        const eventDepth = ds.yScale.getValueForPixel(position['y']);
        //look for the layer data for the determined timestamp
        const layerValues = ds._parsed[dataIdx].layer;

        // find the thermistor depth index
        const tdIndex = ds.yScale.options.thermistorDepths.findIndex((td) => {
          return (
            td ===
            ds.yScale.options.thermistorDepths.reduce((p, c) =>
              Math.abs(c - eventDepth) < Math.abs(p - eventDepth) ? c : p,
            )
          );
        });

        //if we had a depth index then we can build the heat map element data
        if (tdIndex !== -1 && layerValues.length > tdIndex) {
          const customData = {
            type: ds.type,
            data: {
              y: layerValues[tdIndex].depth,
              x: roundedTimestamp,
              v: layerValues[tdIndex].value,
              isValid: layerValues[tdIndex].isValid,
              isMissing: layerValues[tdIndex].isMissing,
              isNan: layerValues[tdIndex].isNan,
            },
            yAxisList: ds._dataset.yAxisList,
            datasetName: ds._dataset.datasetName,
            groupName: ds._dataset.groupName,
            unitLabel: ds._dataset.unitLabel,
            legendColor: ds._dataset.legendColor,
          };

          /*
						The first condition checks if showData is true. If it is we can show data
						The second condition checks if isValid is true AND isMissing is false AND isNaN is false. If it is we can show data.
						The third condition checks if isMissing is false AND isNaN is false. If it is we can show data
						*/
          if (
            showInvalidData ||
            (customData.data.isValid &&
              !customData.data.isMissing &&
              !customData.data.isNan &&
              !customData.data.isMissing &&
              !customData.data.isNan)
          ) {
            // heat map items use the thermistor depth index, because the time or depth index can stay the same we need to create
            // a composite index using bitwise, since the graph still uses index based system to determine if the elements changed
            const compIdx = (dataIdx << 16) | tdIndex;
            items.push({ element, customData, datasetIndex: i, index: compIdx });
          }
        }
      }
    }
  });

  return items;
};

export const isPositionVisible = ({ x, y }, chartArea) => {
  if (x < chartArea.left || x > chartArea.right || y < chartArea.top || y > chartArea.bottom) {
    return false;
  }

  return true;
};

const transformBarColour = (value) => {
  if (isValueEmpty(value)) {
    // return default value
    return RISK_CODE_COLOURS['0'];
  }
  return RISK_CODE_COLOURS[value];
};

const buildYAxis = (dataset) => {
  const transform = dataset.graphConfig.transform;
  const axisTickString = dataset.graphConfig.yAxisTickList;
  let yAxisLabel = dataset.graphConfig.graphAxisName;
  yAxisLabel += dataset.unitLabel.length > 0 ? ' (' + dataset.unitLabel + ')' : '';

  let yAxisOpts = {
    ...yAxisDefaultConfig,
    type: 'linear',
    Id: `yAxis-${dataset.graphConfig.graphAxisName}`,
    suggestedMin: dataset.graphConfig.yAxisMinFlex !== false ? dataset.graphConfig.yAxisMin : null,
    min: dataset.graphConfig.yAxisMinFlex === false ? dataset.graphConfig.yAxisMin : null,
    suggestedMax: dataset.graphConfig.yAxisMaxFlex !== false ? dataset.graphConfig.yAxisMax : null,
    max: dataset.graphConfig.yAxisMaxFlex === false ? dataset.graphConfig.yAxisMax : null,
    title: {
      text: yAxisLabel,
      display: true,
    },
    ticks: {
      ...yAxisDefaultConfig.ticks,
      precision: dataset.graphConfig.yAxisTickDecimalPlaces || 0,
      beginAtZero: false,
      //callback for ticks sets precision
      callback: (tickValue) => {
        if (dataset.graphConfig.yAxisTickDecimalPlaces !== null) {
          const tickPow = Math.pow(10, dataset.graphConfig.yAxisTickDecimalPlaces);
          return (Math.floor(tickValue * tickPow) / tickPow).toFixed(dataset.graphConfig.yAxisTickDecimalPlaces);
        }

        return tickValue;
      },
    },

    display: 'auto',
  };

  if (transform) {
    yAxisOpts = { ...Object.assign(yAxisOpts, getTransformAxisInfo(axisTickString)), display: true };
  }

  return yAxisOpts;
};

export const getTransformAxisInfo = (axisTickString) => {
  const tickList = transformMeaningfulValues(axisTickString);
  const { min, max, count } = getMeaninfulMinMax(tickList);

  const result = {
    min: min,
    max: max,
    ticks: {
      count: count,
      maxTicksLimit: count,
      callback: function (value) {
        if (value == undefined) return null;
        return tickList[value];
      },
    },
  };

  return result;
};

const getMeaninfulMinMax = (meaningfulValues) => {
  const keys = Object.keys(meaningfulValues).filter((key) => !isNaN(parseInt(key)));
  const min = Math.min(...keys, 1);
  const max = Math.max(...keys);
  const count = keys.length;
  return { min, max, count };
};

const transformMeaningfulValues = (meaningfulValues) => {
  const transformedValues = {};
  const usedOrders = new Set();

  meaningfulValues.forEach((value) => {
    let { DISPLAY_ORDER, DISPLAYED_MEANING } = value;

    if (DISPLAY_ORDER != null) {
      transformedValues[DISPLAY_ORDER] = DISPLAYED_MEANING;
      usedOrders.add(DISPLAY_ORDER);
    }
  });

  meaningfulValues.forEach((value) => {
    let { DISPLAY_ORDER, DISPLAYED_MEANING } = value;

    if (DISPLAY_ORDER == null) {
      let nextOrder = 1;
      while (usedOrders.has(nextOrder)) {
        nextOrder++;
      }
      transformedValues[nextOrder] = DISPLAYED_MEANING;
      usedOrders.add(nextOrder);
    }
  });
  const allOrders = Array.from(usedOrders);
  const maxOrder = Math.max(...allOrders);

  for (let i = 0; i <= maxOrder; i++) {
    if (!(i in transformedValues)) {
      transformedValues[i] = '';
    }
  }

  return transformedValues;
};

const buildYtickList = (axisTickString) => {
  let ticksList = {};
  const usedOrders = new Set();

  if (isValueNotEmpty(axisTickString)) {
    axisTickString.forEach((t) => {
      const key = t.DISPLAY_ORDER;
      if (key != null) {
        ticksList[key] = t.DISPLAYED_MEANING;
        usedOrders.add(key);
      }
    });
    axisTickString.forEach((t) => {
      if (t.DISPLAY_ORDER == null) {
        let nextKey = 1;
        while (usedOrders.has(nextKey)) {
          nextKey++;
        }
        ticksList[nextKey] = t.DISPLAYED_MEANING;
        usedOrders.add(nextKey);
      }
    });
  } else {
    return null;
  }

  return ticksList;
};

const getYAxis = (dataSets) => {
  const yAxes = dataSets
    .map((ds, idx) => buildYAxis(ds, idx))
    .reduce((a, b) => {
      a[b.Id] = b;
      return a;
    }, {});

  const result = Object.keys(yAxes)
    .map((key, idx) => {
      return [yAxes[key].Id, { ...yAxes[key], position: idx % 2 == 1 ? 'right' : 'left' }];
    })
    .reduce(mapArrayToObjectReducer, {});

  return result;
};

//external called by Frost Probe helper
export const yAxisDefaultConfig = {
  type: 'linear',
  //display: 'auto',
  ticks: {
    maxTicksLimit: 5,
    count: 5,
  },
};

export const defaultBuildOptions = {
  maintainAspectRatio: false,
  animation: { duration: 0 /*general animation time*/ },
  hover: { animationDuration: 0 /*duration of animations when hovering*/ },
  responsiveAnimationDuration: 0 /*animation duration after screen resize*/,
};

export const buildXAxis = (minimum, maximum, type = 'time') => {
  const xAxisOpt = {
    suggestedMin: minimum,
    min: minimum,
    suggestedMax: maximum,
    max: maximum,
    type: type,
    ticks: {
      major: {
        enabled: true,
      },
      maxTicksLimit: 17,
      count: 17,
    },
    time:
      type === 'time'
        ? {
            displayFormats: {
              day: 'MMM d',
              hour: 'HH:mm', // 24-hour format
            },
            unit: 'hour',
          }
        : {},
  };
  return xAxisOpt;
};

//externally called by other pages, frost probe, graph
export const calculateStartDate = (endDate, range) => {
  if (isToday(endDate)) {
    return subDays(new Date(endDate), range + 1).getTime();
  }

  return startOfDay(subDays(new Date(endDate), range)).getTime();
};

const invalid = (ctx, value) => {
  return !ctx.p0.raw.isValid || !ctx.p1.raw.isValid ? value : undefined;
};

function createDiagonalPattern(color = 'black') {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  const size = 10;
  const stroke = 3;
  const strokeOffset = stroke / 2;
  canvas.width = size;
  canvas.height = size;

  context.strokeStyle = color;
  context.lineWidth = stroke;

  // the offset is needed because of antialisaing. We always want to draw outside the edge
  context.moveTo(size / 2 - strokeOffset, -strokeOffset);
  context.lineTo(size + strokeOffset, size / 2 + strokeOffset);
  context.moveTo(-strokeOffset, size / 2 - strokeOffset);
  context.lineTo(size / 2 + strokeOffset, size + strokeOffset);
  context.stroke();

  return context.createPattern(canvas, 'repeat');
}

//externally called by Graph
export const buildGraphData = (selectedDatasets, showInvalidData = false) => {
  // eslint-disable-next-line no-unused-vars
  return selectedDatasets.map((ds, idx) => {
    const dsGraphConfig = ds.graphConfig;
    const graphType = dsGraphConfig.graphType + ''; //do not lowercase here
    const transform = dsGraphConfig.transform;
    const isBarGraph = graphType === 'Bar';

    const dataItem = {
      id: ds.DatasetId,
      label: ds.DatasetId,
      observationTypeId: ds.observationTypeId,
      sensorTypeId: ds.sensorTypeId, //why?
      datasetName: ds.label,
      groupName: ds.group,
      collectionInterval: ds.CollectionIntervalMinutes || 60,
      unitLabel: ds.unitLabel || '',
      legendColor: ds.color,

      //Overrides for unimplemented graph types
      type: graphType.toLowerCase() == 'dotted line' ? 'line' : graphType.toLowerCase(),
      borderDash: graphType.toLowerCase() == 'dotted line' ? [10, 5] : [],

      yAxisID: `yAxis-${dsGraphConfig.graphAxisName}`,
      //displayFormat: isValueEmpty(dsGraphConfig.displayFormat) ? 'F1' : dsGraphConfig.displayFormat,
      displayFormat: dsGraphConfig.displayFormat,
      transform: transform,

      yAxisList: buildYtickList(dsGraphConfig.yAxisTickList),
      // this is used for linking obersvations that need to color the bars based on another obs type
      associatedObservationTypeID: dsGraphConfig.assocObsTypeId,

      spanGaps: showInvalidData,
      segment: {
        borderColor: (ctx) => invalid(ctx, `${ds.color}`),
        borderDash: (ctx) => invalid(ctx, [4, 4]),
      },
      data: [],
      minBarLength: 7,
    };

    //set colors based on default color assigned to dataset
    dataItem.backgroundColor = isBarGraph ? [] : ds.color;
    dataItem.borderColor = isBarGraph ? [] : ds.color;

    //there is no data so return the config we have without data populated
    if (!ds.Data || isValueEmpty(ds.Data)) return dataItem;

    // if there is a transform for a bar graph or and associated observation type the color is an array
    if ((transform && isBarGraph) || !isValueEmpty(dataItem.associatedObservationTypeID)) dataItem.backgroundColor = [];

    dataItem.data = ds.Data.map((point) => {
      let val = point.y === null ? null : isIntStr(point.y) ? Number(point.y) : point.y; //observation value may be a text value
      let valCopy = val; //display original value on tooltip
      let pointColor = ds.color;

      if (transform) {
        pointColor = transformBarColour(!isValueEmpty(dataItem.associatedObservationTypeID) ? point.AssociatedY : val);
      }

      if (isBarGraph) {
        dataItem.backgroundColor.push(point.IsInvalid ? createDiagonalPattern(pointColor) : pointColor);
      } else {
        dataItem.backgroundColor = pointColor;
      }

      if (val !== null) {
        if (dataItem.displayFormat && dataItem.displayFormat.length > 0) {
          if (isIntStr(point.Value)) {
            val = numFormat(point.Value, dataItem.displayFormat);
            valCopy = val;
          }
        }
        //if yAxisList not null, always transform obs value to display order with displayedMeaning regardless transform
        if (isValueNotEmpty(dataItem.yAxisList)) val = point.DisplayOrder;
      }

      return {
        x: Number(point.x),
        y: !point.IsInvalid || showInvalidData ? val : null,
        originalY: point.y,
        displayY: !point.IsInvalid || showInvalidData ? valCopy : null,
        isValid: !point.IsInvalid,
        backgroundColor: dataItem.backgroundColor,
        borderColor: dataItem.borderColor,
        isNan: point.IsNan,
        isMissing: point.IsMissing,
      };
    });

    return dataItem;
  });
};

export const buildOptions = (
  selectedDatasets,
  graphId,
  startDate,
  endDate,
  panComplete,
  zoomComplete,
  showInvalidData = false,
) => {
  //const dataSets = getNonImageDatasets([...selectedDatasets]);
  return {
    ...defaultBuildOptions,
    id: graphId,
    showInvalidData: showInvalidData,
    plugins: {
      htmlLegend: {
        containerID: graphId + '_Legend',
      },
      legend: {
        // Creating a custom HTML legend rather than using built-in chart.js one.
        display: false,
      },
      tooltip: {
        enabled: false,
        position: 'myCustomPositioner',
        external: externalTooltipHandler,
      },
      zoom: {
        zoom: {
          wheel: {
            enabled: true,
            modifierKey: 'shift',
          },
          pinch: {
            enabled: true,
          },
          mode: 'x',
          // no refresh on zoom it's doable but requires it's own custom function and
          // in my usage appears to be uneccesary as a quick pan will grab more data.
          onZoomComplete: zoomComplete,
        },
        pan: {
          enabled: true,
          mode: 'x',
          modifierKey: 'shift',
          onPanComplete: panComplete,
        },
      },
    },
    scales: {
      ...getYAxis(selectedDatasets),
      xAxis: buildXAxis(startDate, endDate),
    },
    interaction: {
      mode: 'timestamp',
      /*mode: 'index',
			intersect: true,*/
    },
    elements: {
      point: {
        radius: 1, // 1 for single point
        hitRadius: 5,
      },
    },
  };
};

export const syncronizeObservationData = (datasets) => {
  //what's the first point timepoint (x) in each dataset.Data?
  const firstPoints = datasets
    .map((ds) => {
      if (!ds.Data || !ds.Data[0] || !ds.Data[0].x) return null;
      return ds.Data[0].x;
    })
    .filter((v, i, s) => s.indexOf(v) === i)
    .sort((a, b) => a - b);

  if (firstPoints.length > 1) {
    //they don't line up, first one is the earliest
    //find out which datasets are out of sync
    const firstPoint = firstPoints[0];

    //get the datasets that are out of sync
    const outOfSync = datasets.filter(
      (d) =>
        datasets
          .map((ds) => {
            if (!ds.Data) return [];
            return ds.Data.find((d) => d.x === firstPoint) ? ds.DatasetId : undefined;
          })
          .filter((v) => v !== undefined)
          .indexOf(d.DatasetId) < 0,
    );

    outOfSync.map((o) => {
      if (!o.Data || firstPoint === null) return;
      const mins = isValueEmpty(o.CollectionIntervalMinutes) ? 60 : o.CollectionIntervalMinutes;
      const minsStamp = 60 * mins * 1000;
      const missingPoints = (o.Data[0].x - firstPoint) / 1000 / 60 / mins;
      let newTimestamp = parseInt(firstPoint) + minsStamp;
      const obsData = [...o.Data] || [];

      for (let x = 1; x <= missingPoints; x++) {
        obsData.push({
          AssociatedY: null,
          ConfidenceLevel: '0',
          ConfidenceLevelName: undefined,
          DisplayFormat: 'F1',
          IsInvalid: false,
          Value: null,
          x: newTimestamp,
          y: null,
          z: null,
        });
        newTimestamp + minsStamp;
      }
      obsData.sort((a, b) => {
        return a.x > b.x ? -1 : a.x < b.x ? 1 : 0;
      });

      datasets.find((ds) => ds.DatasetId === o.DatasetId).Data = obsData;
    });
  }
  return datasets;
};

//externally called by Graph & Frost Probe helper
export const fillObservationData = (datasets, property) => {
  let data = [];
  datasets.map((obs) => {
    data = [...data, ...(obs.Data || [])];
  });

  //combine the observation data set array down to timestamps then
  // reduce the arrays into single one and filter to remove dupes
  const timeStamps = Object.keys(data)
    .map((k) => data[k][property])
    .reduce((acc, cur) => acc.concat(cur), [])
    .filter((val, idx, self) => self.indexOf(val) === idx);

  return datasets.map((obs) => {
    let result = []; //new results array
    let diff = []; //diff array

    const obsData = obs.Data || [];
    // get the differences from the timestamps to the observation data items

    if (obsData.length > 0) {
      diff = timeStamps.filter((x) => !obsData.some((y) => x === y[property]));
    }

    //add the observations we have to the result array
    obsData.forEach((val) => {
      result.push(val);
    });

    //if we have differences then we'll iterate thru them and push
    // them to the results array
    if (diff.length > 0) {
      diff.forEach((v) => {
        result.push({
          AssociatedY: null,
          ConfidenceLevel: '0',
          ConfidenceLevelName: undefined,
          DisplayFormat: 'F1',
          IsInvalid: false,
          Value: null,
          x: v,
          y: null,
          z: null,
        });
      });
    }

    //the results need to be sorted, based on the property.
    result.sort((a, b) => {
      return a[property] > b[property] ? -1 : a[property] < b[property] ? 1 : 0;
    });

    return { ...obs, Data: result };
  });
};

// need to add some special cases for the precision resistor observation
// it's a Frost Probe type but functions as a regular dataset
export const getRegularDatasets = (datasets, precisionResistorObservation = null) => {
  return [...(datasets || [])].filter(
    (e) =>
      e.type === DATASET_TYPES.Regular ||
      e.type === DATASET_TYPES.ForecastData ||
      e.observationTypeId == precisionResistorObservation?.ObservationTypeId,
  );
};

export const getImageDatasets = (datasets) => {
  return [...(datasets || [])].filter((e) => e.type === DATASET_TYPES.Image);
};

export const getFrostProbeDatasets = (datasets, precisionResistorObservation = null) => {
  //we need to create a deep copy to operate on
  let frostProbeDatasets = JSON.parse(JSON.stringify(datasets));

  frostProbeDatasets = [...(datasets || [])].filter(
    (e) =>
      e.type === DATASET_TYPES.FrostProbe && e.observationTypeId != precisionResistorObservation?.ObservationTypeId,
  );

  // WEAV-10159, this code will join the Data to preserve continuity for the Heat Map
  // when the sensor Data Segment was changed
  const uniqueLayers = frostProbeDatasets.map((ds) => ds.SensorLayerNumber).filter((i, x, a) => a.indexOf(i) === x);

  const maxSLN = Math.max(...uniqueLayers.filter((n) => n !== 0));

  frostProbeDatasets = uniqueLayers.map((u) => {
    const sameDS = frostProbeDatasets.filter((f) => f.SensorLayerNumber === u);

    const singleDS =
      u === maxSLN
        ? sameDS.reduce((m, c) => {
            return c.elevation < m.elevation ? c : m;
          })
        : sameDS.find((f) => f.SensorLayerNumber === u);

    let mergedData = sameDS.reduce((acc, current) => {
      return current.Data ? acc.concat(current.Data) : acc;
    }, []);

    mergedData = mergedData.length > 0 ? mergedData : null;
    return {
      ...singleDS,
      Data: mergedData,
    };
  });

  return frostProbeDatasets.sort((a, b) => {
    return a.SensorLayerNumber < b.SensorLayerNumber ? -1 : 1;
  });
};

export const getGraphMargin = (scales, direction) => {
  const yAxis = Object.keys(scales)
    .map((s) => scales[s])
    .filter((a) => a.axis.toLowerCase() == 'y');

  return yAxis
    .filter((a) => a.position.toLocaleLowerCase() == direction)
    .map((a) => a.width)
    .reduce((a, b) => a + b + 5, 10);
};
